import React, { useEffect, useRef } from 'react';
import { Eye, Github, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export interface ProjectCardProps {
  title: string;
  description: string;
  image: string;
  technologies: string[];
  demoUrl?: string;
  repoUrl?: string;
  featured?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  description,
  image,
  technologies,
  demoUrl,
  repoUrl,
  featured = false,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            entry.target.classList.remove('opacity-0');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Card 
      ref={cardRef}
      className={`opacity-0 transform transition-all duration-700 hover:scale-105 overflow-hidden border ${
        featured ? 'border-portfolio-accent border-2' : ''
      }`}
    >
      <div className="relative overflow-hidden group h-48 md:h-56">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-4">
          <div className="flex gap-2">
            {demoUrl && (
              <a href={demoUrl} target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant="secondary" className="bg-white text-portfolio-blue hover:bg-portfolio-blue hover:text-white">
                  <Eye size={16} className="mr-1" /> Demo
                </Button>
              </a>
            )}
            {repoUrl && (
              <a href={repoUrl} target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant="secondary" className="bg-white text-portfolio-blue hover:bg-portfolio-blue hover:text-white">
                  <Github size={16} className="mr-1" /> Code
                </Button>
              </a>
            )}
          </div>
          
          {featured && (
            <Badge className="bg-portfolio-accent text-white">Featured</Badge>
          )}
        </div>
      </div>
      
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-xl font-semibold">{title}</h3>
          {repoUrl && (
            <a href={repoUrl} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-portfolio-blue transition-colors">
              <ExternalLink size={18} />
            </a>
          )}
        </div>
        
        <p className="text-gray-600 mb-4 line-clamp-2">{description}</p>
        
        <div className="flex flex-wrap gap-2">
          {technologies.map((tech) => (
            <Badge key={tech} variant="secondary" className="bg-portfolio-blue/10 text-portfolio-blue hover:bg-portfolio-blue hover:text-white">
              {tech}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectCard;
